import { useState } from 'react';
import '../styles/CodeShowcase.css';

const KEYWORD_RE =
  /\b(import|from|function|const|let|var|async|await|return|contract|interface|struct|fn|func|use|def|class|export|public|private|external|internal|new|print|println!|main|None|package|mut)\b/;

function highlightLine(line) {
  const regex = new RegExp(
    `(\\/\\/.*|#(?!\\[).*)|("(?:[^"\\\\]|\\\\.)*")|(${KEYWORD_RE.source})|(\\b\\d+(?:\\.\\d+)?\\b)`,
    'g'
  );

  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: line.slice(lastIndex, match.index), cls: '' });
    }
    let cls = '';
    if (match[1]) cls = 'code-comment';
    else if (match[2]) cls = 'code-string';
    else if (match[3]) cls = 'code-keyword';
    else if (match[5]) cls = 'code-number';
    parts.push({ text: match[0], cls });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < line.length) {
    parts.push({ text: line.slice(lastIndex), cls: '' });
  }
  return parts;
}

const CODE_SAMPLES = {
  Solidity: [
    'import "@flarenetwork/flare-periphery-contracts/flare/ContractRegistry.sol";',
    'import "@flarenetwork/flare-periphery-contracts/flare/FtsoV2Interface.sol";',
    '',
    'contract FtsoV2Consumer {',
    '    FtsoV2Interface internal ftsoV2;',
    '',
    '    function getFlrUsdPrice() external returns',
    '        (uint256 value, int8 decimals, uint64 timestamp) {',
    '        ftsoV2 = ContractRegistry.getFtsoV2();',
    '        bytes21 flrUsdId = 0x01464c522f5553440000000000000000000000;',
    '        return ftsoV2.getFeedById(flrUsdId);',
    '    }',
    '}',
  ],
  JavaScript: [
    'import { Web3 } from "web3";',
    '',
    'export async function main() {',
    '  const w3 = new Web3(RPC_URL);',
    '  const ftsov2 = new w3.eth.Contract(JSON.parse(ABI), FTSOV2_ADDRESS);',
    '',
    '  const flrUsdId = "0x01464c522f5553440000000000000000000000";',
    '  const res = await ftsov2.methods.getFeedById().call();',
    '',
    '  console.log(',
    '    "Value: %s, Decimals: %s, Timestamp: %s", res["0"], res["1"]',
    '  );',
    '}',
  ],
  Python: [
    'import asyncio',
    'from web3 import AsyncHTTPProvider, AsyncWeb3',
    '',
    'async def main() -> None:',
    '    w3 = AsyncWeb3(AsyncHTTPProvider(RPC_URL))',
    '    ftsov2 = w3.eth.contract(address=FTSOV2_ADDRESS, abi=ABI)',
    '',
    '    flr_usd_id = "0x01464c522f5553440000000000000000000000"',
    '    res = await ftsov2.functions.getFeedById(flr_usd_id).call()',
    '',
    '    print(f"Value: {res[0]}, Decimals: {res[1]}, Timestamp: {res[2]}")',
  ],
  Rust: [
    'use alloy::{providers::ProviderBuilder, sol};',
    'use eyre::Result;',
    '',
    'sol!(FtsoV2, "abi/FtsoV2.json");',
    '',
    '#[tokio::main]',
    'async fn main() -> Result<()> {',
    '    let data = FtsoV2::new(FTSOV2_ADDRESS, ProviderBuilder::new())',
    '        .getFeedById("0x01464c522f5553440000000000000000000000")',
    '        .call()',
    '        .await?;',
    '    println!("Value:{}, Decimals:{}, Timestamp:{}", data._0, data._1, data._2);',
    '    Ok(())',
    '}',
  ],
  Go: [
    'import (',
    '    "github.com/ethereum/go-ethereum/accounts/abi/bind"',
    '    "github.com/ethereum/go-ethereum/ethclient"',
    ')',
    '',
    'func FtsoV2Consumer() {',
    '    client, _ := ethclient.Dial(RPC_URL)',
    '    ftsov2, _ := NewFtsoV2(FTSOV2_ADDRESS, client)',
    '    flrUsdId := "0x01464c522f5553440000000000000000000000"',
    '    var res []interface{}',
    '    ftsov2.FtsoV2Caller.contract.Call(nil, &res, "getFeedById", flrUsdId)',
    '    println("Value:", res[0], "Decimals:", res[1], "Timestamp:", res[2])',
    '}',
  ],
};

const TABS = Object.keys(CODE_SAMPLES);

function CodeShowcase() {
  const [activeTab, setActiveTab] = useState('Solidity');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const raw = CODE_SAMPLES[activeTab].join('\n');
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <section className="code-showcase">
      <div className="code-showcase__card">
        <div className="code-showcase__left">
          <h2 className="code-showcase__title">Build on Flare</h2>
          <p className="code-showcase__subtext">
            Leverage Flare's full-stack data solutions in your decentralized application.
          </p>
          <div className="code-showcase__buttons">
            <button className="code-showcase__btn code-showcase__btn--primary">
              Developer Resources
            </button>
            <button className="code-showcase__btn code-showcase__btn--secondary">
              Learn about Flare
            </button>
          </div>
        </div>

        <div className="code-showcase__right">
          <div className="code-showcase__tabs">
            {TABS.map((tab) => (
              <button
                key={tab}
                className={`code-showcase__tab ${activeTab === tab ? 'code-showcase__tab--active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="code-showcase__panel">
            <pre className="code-showcase__pre">
              {CODE_SAMPLES[activeTab].map((line, i) => (
                <div className="code-line" key={i}>
                  <span className="code-line__num">{i + 1}</span>
                  <span className="code-line__content">
                    {highlightLine(line).map((part, j) => (
                      <span key={j} className={part.cls}>{part.text}</span>
                    ))}
                  </span>
                </div>
              ))}
            </pre>

            <button className="code-showcase__copy" onClick={handleCopy}>
              {copied ? 'copied' : 'copy'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CodeShowcase;